import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router";
import { BenachrichtigungenBegleiten } from "./benachrichtigung/Begleitung";
import { darstellungAnwenden, DARSTELLUNG_SCHLUESSEL, Einstellungen } from "./benachrichtigung/Einstellungen";
import { MehrOhneDrueckjagd } from "./design/MehrOhneDrueckjagd";
import { AngabenZumJagdzustand } from "./drueckjagd/AngabenZumJagdzustand";
import { AnsichtDerDrueckjagd } from "./drueckjagd/AnsichtDerDrueckjagd";
import { useAusloeser } from "./drueckjagd/Ausloeser";
import { useJagdzustand } from "./drueckjagd/jagdzustand";
import { BenachrichtigungOeffnen } from "./drueckjagd/BenachrichtigungOeffnen";
import { Einteilung } from "./drueckjagd/Einteilung";
import { Freigabe, Kontingentstand, Kontrollliste, NachweiskontrolleDerPerson, Pruefprotokoll } from "./drueckjagd/Freigabe";
import { MeineDrueckjagden } from "./drueckjagd/MeineDrueckjagden";
import { Mehr } from "./drueckjagd/Mehr";
import { Einweisung, HundeImTreiben, MeineAufgaben, Notfallinformationen, Standbesetzung, TeilnehmeruebersichtDerDrueckjagd } from "./drueckjagd/Mitwirkende";
import { Planung } from "./drueckjagd/Planung";
import { RevierkarteDerDrueckjagd } from "./drueckjagd/Revierkarte";
import { Startansicht } from "./drueckjagd/Startansicht";
import { Standansicht } from "./drueckjagd/Standansicht";
import { Kanal } from "./nachrichten/Kanal";
import { Kanaele } from "./nachrichten/Kanaele";
import { MediumOeffnen } from "./nachrichten/MediumOeffnen";
import { Anmeldung } from "./person-und-zugang/Anmeldung";
import { Einladung } from "./person-und-zugang/Einladung";
import { KontaktBitte } from "./person-und-zugang/KontaktDialog";
import { Kontoansicht } from "./person-und-zugang/Kontoansicht";
import { Hunde } from "./person-und-zugang/Hunde";
import { Registrierung } from "./person-und-zugang/Registrierung";
import { Umzug, Umzugsmodus } from "./person-und-zugang/Umzug";
import { Wiederherstellung } from "./person-und-zugang/Wiederherstellung";
import { firebaseDienste } from "./plattform/firebase";
import { Meldungen, MeldungenListe } from "./plattform/Meldungen";
import { OfflineBegleitung } from "./plattform/Offline";
import type { Rahmen, RahmenProps } from "./plattform/rahmen";
import { SitzungAnbieter, useSitzung } from "./plattform/sitzung";
import { Revier } from "./revier/Revier";
import { Reviere } from "./revier/Reviere";
import { Teilnehmer } from "./revier/Teilnehmer";
import { Teilnehmeruebersicht } from "./revier/Teilnehmeruebersicht";
import { Staende } from "./revier/Staende";
import { Notfallkontakte, Rettungspunkte, Revierkarte, Revierregeln, Sicherheitsbestimmungen } from "./revier/Stammdaten";
import { Bergeliste } from "./schuss/Bergeliste";
import { SchussErgaenzen } from "./schuss/Ergaenzen";
import { KarteDerLeitung } from "./schuss/Karte";
import { LiegeortAblauf } from "./schuss/LiegeortAblauf";
import "./schuss/meldungen";
import { HundefuehrerVorschlag, OffeneSuchen } from "./schuss/OffeneSuchen";
import { SchussAblauf } from "./schuss/SchussAblauf";
import "./schuss/startansicht";
import { Abholliste } from "./lage/Abholliste";
import { HundAblauf } from "./lage/HundAblauf";
import { Lagebild } from "./lage/Lagebild";
import { NotrufAblauf } from "./lage/NotrufAblauf";
import { Notrufe } from "./lage/Notrufe";
import "./lage/startansicht";
import "./drueckjagd/hauptaktionen";
import { WildsichtungAblauf } from "./lage/WildsichtungAblauf";
import { Jagddokumentation } from "./strecke/Jagddokumentation";
import { Jagdstatistik } from "./strecke/Jagdstatistik";
import "./strecke/startansicht";
import { Strecke } from "./strecke/Strecke";

/**
 * Routen der PWA (ADR-0027). Ohne Sitzung zeigt jeder geschützte Pfad die Anmeldung (oberflaeche.md §3.1, §8.7);
 * Einladung, Registrierung und Wiederherstellung gehen ohne Sitzung.
 */
function Geschuetzt({ children }: { children: React.ReactNode }) {
  const { status } = useSitzung();
  if (status === "laedt") return <div className="ansicht ansicht--neutral" aria-busy="true" />;
  if (status === "ohne") return <Anmeldung />;
  return <>{children}</>;
}

/** „Meldungen“ ab der Startansicht: dieselbe Liste in einer Ansicht der Drückjagd mit Zustandsband (oberflaeche.md §3.1, V-7). */
function MeldungenDerDrueckjagd() {
  const { id = "" } = useParams();
  return <AnsichtDerDrueckjagd drueckjagdId={id} name="Meldungen"><MeldungenListe /></AnsichtDerDrueckjagd>;
}

/**
 * Rahmen einer Ansicht der Drückjagd für die anderen Kontexte (plattform/rahmen.ts; oberflaeche.md §3.2, F-080): Kanäle,
 * Kanal, Lage, Schuss, Strecke. Mit `ausloeser` steht der Auslöser der Leitung fest unten (Lagebild, §4.3, §6); sein
 * Bestätigungsdialog steht dann an Stelle der Ansicht (design/README §9.5).
 */
function AnsichtMitAusloeser(p: RahmenProps) {
  const anzeige = useJagdzustand(p.drueckjagdId);
  const ausloeser = useAusloeser(p.drueckjagdId, anzeige);
  if (ausloeser.dialog) return <>{ausloeser.dialog({ name: p.name })}</>;
  return <AnsichtDerDrueckjagd {...p} unten={<>{p.unten}{ausloeser.kachel}</>} />;
}

const rahmenDerDrueckjagd: Rahmen = (p) => (p.ausloeser ? <AnsichtMitAusloeser {...p} /> : <AnsichtDerDrueckjagd {...p} />);

export function App() {
  useEffect(() => darstellungAnwenden(localStorage.getItem(DARSTELLUNG_SCHLUESSEL)), []);
  // Alte Adresse im Umzugsmodus (K103 (5), V-28): nur der Hinweis; andere Pfade leitet schon der Start weiter (main.tsx).
  const umzug = firebaseDienste().konfig.umzug;
  if (umzug) return <SitzungAnbieter><Umzugsmodus ziel={umzug.ziel} /></SitzungAnbieter>;
  return (
    <SitzungAnbieter>
      <OfflineBegleitung />
      <BenachrichtigungenBegleiten />
      <BrowserRouter>
        <Routes>
          <Route path="/einladung" element={<Einladung />} />
          <Route path="/registrierung" element={<Registrierung />} />
          <Route path="/wiederherstellung" element={<Wiederherstellung />} />
          <Route path="/umzug" element={<Umzug />} />
          <Route path="/" element={<Geschuetzt><MeineDrueckjagden vorne={<KontaktBitte />} /></Geschuetzt>} />
          <Route path="/kanaele" element={<Geschuetzt><Kanaele rahmen={rahmenDerDrueckjagd} /></Geschuetzt>} />
          <Route path="/kanaele/:kanalId" element={<Geschuetzt><Kanal rahmen={rahmenDerDrueckjagd} /></Geschuetzt>} />
          <Route path="/medien/kanaele/:kanalId/nachrichten/:nachrichtId/:datei" element={<Geschuetzt><MediumOeffnen /></Geschuetzt>} />
          <Route path="/reviere" element={<Geschuetzt><Reviere /></Geschuetzt>} />
          <Route path="/reviere/:revierId" element={<Geschuetzt><Revier /></Geschuetzt>} />
          <Route path="/reviere/:revierId/teilnehmer" element={<Geschuetzt><Teilnehmeruebersicht /></Geschuetzt>} />
          <Route path="/reviere/:revierId/staende" element={<Geschuetzt><Staende /></Geschuetzt>} />
          <Route path="/reviere/:revierId/revierregeln" element={<Geschuetzt><Revierregeln /></Geschuetzt>} />
          <Route path="/reviere/:revierId/sicherheitsbestimmungen" element={<Geschuetzt><Sicherheitsbestimmungen /></Geschuetzt>} />
          <Route path="/reviere/:revierId/notfallkontakte" element={<Geschuetzt><Notfallkontakte /></Geschuetzt>} />
          <Route path="/reviere/:revierId/rettungspunkte" element={<Geschuetzt><Rettungspunkte /></Geschuetzt>} />
          <Route path="/reviere/:revierId/revierkarte" element={<Geschuetzt><Revierkarte /></Geschuetzt>} />
          <Route path="/reviere/:revierId/teilnehmer/:teilnehmerId" element={<Geschuetzt><Teilnehmer /></Geschuetzt>} />
          <Route path="/meldungen" element={<Geschuetzt><Meldungen /></Geschuetzt>} />
          <Route path="/konto" element={<Geschuetzt><Kontoansicht /></Geschuetzt>} />
          <Route path="/konto/hunde" element={<Geschuetzt><Hunde /></Geschuetzt>} />
          <Route path="/einstellungen" element={<Geschuetzt><Einstellungen /></Geschuetzt>} />
          <Route path="/mehr" element={<Geschuetzt><MehrOhneDrueckjagd /></Geschuetzt>} />
          <Route path="/drueckjagd/:id" element={<Geschuetzt><Startansicht /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/jagdzustand" element={<Geschuetzt><AngabenZumJagdzustand /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/mehr" element={<Geschuetzt><Mehr /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/meldungen" element={<Geschuetzt><MeldungenDerDrueckjagd /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/planung" element={<Geschuetzt><Planung /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/einteilung" element={<Geschuetzt><Einteilung /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/teilnehmer" element={<Geschuetzt><TeilnehmeruebersichtDerDrueckjagd /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/einweisung" element={<Geschuetzt><Einweisung /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/aufgaben" element={<Geschuetzt><MeineAufgaben /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/notfallinformationen" element={<Geschuetzt><Notfallinformationen /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/standbesetzung" element={<Geschuetzt><Standbesetzung /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/standansicht" element={<Geschuetzt><Standansicht /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/revierkarte" element={<Geschuetzt><RevierkarteDerDrueckjagd /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/hunde" element={<Geschuetzt><HundeImTreiben /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/freigabe" element={<Geschuetzt><Freigabe /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/kontingentstand" element={<Geschuetzt><Kontingentstand /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/kontrollliste" element={<Geschuetzt><Kontrollliste /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/kontrollliste/:teilnehmer" element={<Geschuetzt><NachweiskontrolleDerPerson /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/pruefprotokoll" element={<Geschuetzt><Pruefprotokoll /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/schuss" element={<Geschuetzt><SchussAblauf rahmen={rahmenDerDrueckjagd} /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/liegeort" element={<Geschuetzt><LiegeortAblauf rahmen={rahmenDerDrueckjagd} /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/offene-suchen" element={<Geschuetzt><OffeneSuchen rahmen={rahmenDerDrueckjagd} /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/offene-suchen/:schuss" element={<Geschuetzt><HundefuehrerVorschlag rahmen={rahmenDerDrueckjagd} /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/bergeliste" element={<Geschuetzt><Bergeliste rahmen={rahmenDerDrueckjagd} /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/karte" element={<Geschuetzt><KarteDerLeitung rahmen={rahmenDerDrueckjagd} /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/ergaenzen/:schuss" element={<Geschuetzt><SchussErgaenzen rahmen={rahmenDerDrueckjagd} /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/notruf" element={<Geschuetzt><NotrufAblauf rahmen={rahmenDerDrueckjagd} /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/hund-vermisst" element={<Geschuetzt><HundAblauf rahmen={rahmenDerDrueckjagd} meldung="Hund vermisst" /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/hund-zurueck" element={<Geschuetzt><HundAblauf rahmen={rahmenDerDrueckjagd} meldung="Hund zurück" /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/hund-gesichtet" element={<Geschuetzt><HundAblauf rahmen={rahmenDerDrueckjagd} meldung="Hund gesichtet" /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/wildsichtung" element={<Geschuetzt><WildsichtungAblauf rahmen={rahmenDerDrueckjagd} /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/abholliste" element={<Geschuetzt><Abholliste rahmen={rahmenDerDrueckjagd} /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/lagebild" element={<Geschuetzt><Lagebild rahmen={rahmenDerDrueckjagd} /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/notrufe" element={<Geschuetzt><Notrufe rahmen={rahmenDerDrueckjagd} /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/strecke" element={<Geschuetzt><Strecke rahmen={rahmenDerDrueckjagd} /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/jagdstatistik" element={<Geschuetzt><Jagdstatistik rahmen={rahmenDerDrueckjagd} /></Geschuetzt>} />
          <Route path="/drueckjagd/:id/jagddokumentation" element={<Geschuetzt><Jagddokumentation rahmen={rahmenDerDrueckjagd} /></Geschuetzt>} />
          <Route path="/benachrichtigung/*" element={<Geschuetzt><BenachrichtigungOeffnen /></Geschuetzt>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </SitzungAnbieter>
  );
}
